<template>
  <div class="app-menu fs">
    <el-menu
      class="menus"
      :unique-opened="false"
      :default-active="activedMenuName"
      :default-openeds="openedMenuNames"
      :collapse="collapse"
      background-color="#0e1524"
      text-color="#909399"
      active-text-color="#fff"
      @select="handleSelect"
    >
      <template v-for="menu in rootMenus" :key="menu.name">
        <menu-folder v-if="menu.children?.length" :menu="menu" :collapse="collapse"></menu-folder>
        <menu-item v-else :menu="menu" :collapse="collapse"></menu-item>
      </template>
    </el-menu>
  </div>
</template>

<script setup lang="ts">
import { useHostAppInstance, computed, ref, onMounted, onBeforeRouteUpdate } from '@zto/zpage'

import type { NavMenuItem } from '@zto/zpage'

import MenuTitle from './menu-title.vue'
import MenuFolder from './menu-folder.vue'
import MenuItem from './menu-item.vue'

const props = defineProps<{
  collapse?: boolean
}>()

const app = useHostAppInstance()

const { appStore, pagesStore } = app.stores
const router = app.router
const route = app.useRoute()

// 最多导航条
const maxNavs = app.useAppConfig('menu.maxNavs', 10)
const { Message } = app.useMessage()

const openedMenuNames = ref<Array<string>>([])
const activedMenuName = ref<string>('')

const navMenu = computed(() => appStore.navMenu)

const rootMenus = computed(() => {
  const menus = navMenu.value.menus || []
  return menus
})

onMounted(() => {
  _onRouteChange(route)
})

onBeforeRouteUpdate(async (to, from) => {
  _onRouteChange(to)
})

/**
 * 选中菜单时触发
 */
function handleSelect(index: string, indexPath: string, item: any) {
  const route = router.getRouteByName(index)

  if (!route) {
    Message.warning(`未找到指定页面，请联系管理员！`)
    return
  }

  const meta = route?.meta

  if (meta?.cache !== false) {
    const navPages = pagesStore.navPages
    if (navPages && navPages.length >= maxNavs) {
      Message.warning(`至多打开${maxNavs}个页面,请关闭不使用的页面后再次尝试！`)
      return
    }
  }

  // 使用setTimeout防止菜单卡顿
  setTimeout(() => {
    router.goto(meta)
  }, 5)
}

/**
 * 路由改变时触发
 */
function _onRouteChange(route: any) {
  const menuName = String(route.name)

  activedMenuName.value = menuName

  const allMenus = navMenu.value.menus as NavMenuItem[]
  const currentMenu = allMenus.find(it => it.name === menuName)

  if (currentMenu) {
    openedMenuNames.value = _getParentMenuNames(currentMenu, allMenus, [])
  } else {
    openedMenuNames.value = []
  }
}

// 获取当前路由所有父节点名称
function _getParentMenuNames(menu: NavMenuItem, allMenus: NavMenuItem[], cached: string[]) {
  if (!menu.parentId) return cached

  const parentMenu = allMenus.find(it => it.id === menu.parentId)
  if (!parentMenu) return cached

  cached.push(parentMenu.name)

  _getParentMenuNames(parentMenu, allMenus, cached)

  return cached
}
</script>

<style lang="scss" scoped>
.app-menu {
  width: 100%;
  height: 100%;
  padding-top: 10px;
  border-right: 0;
}

:deep(.menu-item),
:deep(.menu-folder > .el-sub-menu__title) {
  &:hover {
    background: #38486d !important;

    & > .menu-title,
    & > i {
      color: var(--primary-color);

      i {
        color: var(--primary-color);
      }
    }
  }
}

:deep(.el-menu) {
  border-right: 0;

  .el-menu-item {
    height: 40px;
    line-height: 40px;
    min-width: calc(100% - 1px);
    font-size: 14px;

    .menu-title {
      flex: 1;
    }

    &.is-active {
      background: url(/images/menu-bg.png) left top no-repeat;

      .img-icon {
        opacity: 1;
      }
    }
  }

  .el-sub-menu {
    .el-menu-item {
      min-width: 100px;
      padding-right: 20px;
    }
  }

  .el-sub-menu__title {
    display: flex;
    align-items: center;
    font-size: 14px;
    height: 50px;
    line-height: 50px;

    .menu-title {
      flex: 1;
    }

    * {
      vertical-align: initial;
    }
  }
}
</style>
